import { useNamespace, ContactSellerModal, getProjectPromise } from '@bark/bark-core';
import BuyerApi from '../helpers/buyer-api';
import ListUtility from '../helpers/list-utility';
import InitiateNewBark from '../helpers/initiate-new-bark';
import MasterBindings from '../helpers/misc/master';
import {
	registerHBPartials,
	registerSplitListPartials,
} from '../helpers/template-management';

/**
 * Waits for a specified number of seconds before polling the api for the project status.
 *
 * @param {string} projectId The hashed project id
 * @param {string} hashedSessionId The session id for the buyer
 * @param {number} delayInSeconds The delay in seconds before the API call.
 */
async function getProjectStatusAfterDelay(projectId, hashedSessionId, delayInSeconds) {
	const checkedProjectStatusKey = `checkedProjectStatusKey_${projectId}`;

	try {
		if (localStorage.getItem(checkedProjectStatusKey)) return;
	} catch (e) {
		bugsnagClient.notify(e);
	}

	// Wait for the specified delay
	await new Promise((resolve) => setTimeout(resolve, delayInSeconds * 1000));

	try {
		localStorage.setItem(checkedProjectStatusKey, 'true');
	} catch (e) {
		bugsnagClien.notify(e);
	}

	return await getProjectPromise(projectId, hashedSessionId);
}

function reloadPageIfRequired({ awaiting_detail, is_open, rejected }) {
	if (awaiting_detail || rejected || !is_open) {
		window.location.reload();
	}
}

async function checkModerationStatusAndReloadIfNecessary(projectId, sessionId) {
	try {
		const project = await getProjectStatusAfterDelay(projectId, sessionId, 15);

		if (typeof project === 'undefined') {
			return;
		}

		reloadPageIfRequired(project.data.attributes.state);
	} catch (e) {
		bugsnagClient.notify(e);
		return false;
	}
}

export default async function () {
	await useNamespace('buyer_seller-list_seller-card');

	const buyerApi = BuyerApi();

	const state = {
		shouldAnimate: false,
		isAnimating: false,
		shouldShowRequestQuotesFromAll: true,
		loadingStateIndex: 0,
		fullSellerList: [],
		projectDetail: null,
		projectDetailMeta: null,
		buyerDetails: null,
		bookings: null,
		bpvid: null,
		moreSuggestionsAvailable: false,
	};

	let listUtilityHandler = await ListUtility('', state);

	const {
		addManagedSkeletonContainer,
		getDataForSellerPageAndRender,
		bindListeners,
		handleProjectDataLoadedSteps,
		getProjectHash,
		getHashedSessionId,
		redirectToTheProjectList,
		handleInitialLoading,
	} = listUtilityHandler;

	addManagedSkeletonContainer();
	handleInitialLoading();

	let initialLoadPromises = [];

	registerHBPartials([
		'avatar',
		'distance-template',
		'link-view-profile-template',
		'review-stars-and-count-template',
		'elite-pro-icon-template',
		'company-name-template',
		'label-template',
		'label-row-template',
		'primary-mobile-list-card-template',
		'primary-tablet-list-card-template',
		'primary-desktop-list-card-template',
	]);
	registerSplitListPartials();

	buyerApi
		.getProjectPromise(getProjectHash(), getHashedSessionId())
		.then((projectResponse) => {
			let { data: project, meta: projectMeta = null } = projectResponse;
			state.projectDetail = project;
			state.projectDetailMeta = projectMeta;

			if (typeof project === 'undefined') {
				redirectToTheProjectList();
			}

			handleProjectDataLoadedSteps(initialLoadPromises);

			// if we are waiting for suggestions to load, wait until either 10 second fallback or socket message resolves
			Promise.race(initialLoadPromises)
				.catch((err) => {
					bugsnagClient &&
						bugsnagClient.notify(new Error('Error on initial load promise'), {
							metaData: { error: err },
						});
				})
				.finally(() => {
					getDataForSellerPageAndRender(state.bpvid);
				});
		});

	bindListeners({});

	InitiateNewBark().bind();
	MasterBindings();

	checkModerationStatusAndReloadIfNecessary(getProjectHash(), getHashedSessionId());
}
